import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Mail, ShieldCheck, Loader2, BellRing } from 'lucide-react';

// Human-friendly copy for every optional trigger. Kept alongside the UI
// (backend already validates the list) so we don't need an extra endpoint.
const TRIGGER_META = {
  review_received: {
    title: 'Review received',
    desc: 'Fires the instant a buyer posts a review on your IndusFact profile.',
    tierNote: 'Free + Paid — same cadence.',
  },
  contact_reveal_digest: {
    title: 'Contact reveal digest',
    desc: 'A batched summary of buyers who unlocked your contact on your listings.',
    tierNote: 'Free: weekly (Monday 9 AM IST) · Paid: daily (9 AM IST).',
  },
  performance_digest: {
    title: 'Weekly performance digest',
    desc: 'Every Monday: your views, inquiries and reveals in the last 7 days.',
    tierNote: 'Paid users also get top-listing, follower and week-over-week trend.',
  },
  price_drop_alert: {
    title: 'Favourite price-drop alert',
    desc: 'Fires when a listing you have favourited drops in price by 5% or more.',
    tierNote: 'Free: tracked on your first 5 favourites · Paid: unlimited.',
  },
  announcement: {
    title: 'Product announcements',
    desc: 'Occasional email when a new IndusFact capability ships. Never marketing, never recurring.',
    tierNote: 'Switching this off does not affect inquiries, receipts or password emails.',
  },
};

// Transactional triggers users cannot opt out of — displayed as read-only.
const TRANSACTIONAL_INFO = [
  { title: 'Welcome email',            desc: 'Sent once when you sign up.' },
  { title: 'Password reset',           desc: 'When you or an admin request a reset link.' },
  { title: 'Payment receipt',          desc: 'After every successful payment.' },
  { title: 'Listing approved / rejected', desc: 'After moderation decisions on your listings.' },
  { title: 'Listing expiring',         desc: 'Two days and one day before a listing auto-expires.' },
  { title: 'Suspicious login alerts',  desc: 'When your account is locked after failed sign-ins.' },
];

const EmailPreferences = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [optOut, setOptOut] = useState({});
  const [availableTriggers, setAvailableTriggers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications/preferences');
      setOptOut(data.opt_out || {});
      setAvailableTriggers(data.available_triggers || []);
    } catch (e) {
      toast.error('Failed to load preferences', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setEnabled = (trigger, enabled) => {
    // enabled = user wants the email (opt_out=false)
    setOptOut({ ...optOut, [trigger]: !enabled });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch('/notifications/preferences', { opt_out: optOut });
      toast.success('Preferences saved');
    } catch (e) {
      toast.error('Save failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='space-y-5 max-w-3xl' data-testid='email-preferences-page'>
      <div>
        <h1 className='text-2xl font-semibold tracking-tight'>Email preferences</h1>
        <p className='text-muted-foreground text-sm mt-1'>
          Choose which non-transactional emails you want to receive. Transactional
          emails (password reset, payment receipts, moderation outcomes) are always
          sent for account security and cannot be disabled.
        </p>
      </div>

      <Card className='p-5 space-y-4'>
        <div className='flex items-center gap-2'>
          <BellRing className='h-4 w-4 text-primary' />
          <h2 className='font-semibold'>Optional notifications</h2>
        </div>
        {loading ? (
          <div className='py-6 flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' /> Loading…
          </div>
        ) : (
          <div className='divide-y'>
            {availableTriggers.map((trigger) => {
              const meta = TRIGGER_META[trigger] || {
                title: trigger, desc: '', tierNote: '',
              };
              const enabled = !optOut[trigger];
              return (
                <div key={trigger} className='py-3 flex items-start gap-4' data-testid={`pref-row-${trigger}`}>
                  <div className='flex-1 min-w-0'>
                    <div className='font-medium text-sm'>{meta.title}</div>
                    <p className='text-xs text-muted-foreground mt-0.5'>{meta.desc}</p>
                    {meta.tierNote && (
                      <p className='text-[11px] text-muted-foreground mt-1'>
                        <Badge variant='outline' className='mr-1 text-[10px]'>Tier</Badge>
                        {meta.tierNote}
                      </p>
                    )}
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={(v) => setEnabled(trigger, v)}
                    data-testid={`pref-switch-${trigger}`}
                  />
                </div>
              );
            })}
          </div>
        )}
        <div className='pt-2 flex justify-end'>
          <Button onClick={save} disabled={saving || loading} data-testid='pref-save-btn'>
            {saving ? 'Saving…' : 'Save preferences'}
          </Button>
        </div>
      </Card>

      <Card className='p-5 space-y-3'>
        <div className='flex items-center gap-2'>
          <ShieldCheck className='h-4 w-4 text-primary' />
          <h2 className='font-semibold'>Always sent (transactional)</h2>
        </div>
        <p className='text-xs text-muted-foreground'>
          These emails ensure the security and correctness of your account and
          cannot be turned off.
        </p>
        <ul className='space-y-2 text-sm'>
          {TRANSACTIONAL_INFO.map((t) => (
            <li key={t.title} className='flex items-start gap-2'>
              <Mail className='h-4 w-4 text-muted-foreground mt-0.5 shrink-0' />
              <div>
                <div className='font-medium'>{t.title}</div>
                <div className='text-xs text-muted-foreground'>{t.desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};

export default EmailPreferences;
